/**
 * Per-event design system. An event's optional `theme` (events.theme jsonb)
 * overrides the org/tenant brand per field; null = inherit the org brand
 * entirely. Resolution + mode logic live here so the public page and the
 * editor agree.
 *
 * Two schema shapes are supported (recency-precedence: the v2 flat shape is what
 * the in-app «Tema» editor writes today):
 *   - v2 FLAT  { version:2, primary, surface, accent, secondary, display_font,
 *                marble, mode } — drives the full WCAG-safe token set via
 *                generateSemanticTokens. This is the primary path.
 *   - LEGACY per-mode { mode, font_family, light/dark:{primary,bg_color,bg_image} }
 *                — still read for back-compat (older events) via resolveEventDesign.
 */
import { generateSemanticTokens } from './theme-generator'

export interface EventThemeSide {
  /** Key accent / primary action color for this mode. */
  primary?: string | null
  /** Page background color (used when no bg_image). */
  bg_color?: string | null
  /** Background image URL (uploaded or a preset path). */
  bg_image?: string | null
}

export interface EventTheme {
  /**
   * v2 flat: 'light' | 'dark' picks the neutral ramp the palette is derived on.
   * legacy: 'auto' = infer from which sides are filled; else force single/both.
   */
  mode?: 'auto' | 'light' | 'dark' | 'both'
  font_family?: string | null
  light?: EventThemeSide
  dark?: EventThemeSide

  // ── v2 flat palette (written by the in-app «Tema» editor) ──
  /** Schema marker; presence of `primary` also flags the flat shape. */
  version?: 2
  /** Key brand color — primary actions, headings, CTA (e.g. oxblood). */
  primary?: string | null
  /** Page background base; the surface/text/border family derives from it. */
  surface?: string | null
  /** Accent for highlights/dates (e.g. gold). */
  accent?: string | null
  /** Secondary accent (e.g. bunad-green). Stored/preserved but currently has
   *  no consumer — see the note in flatThemeToCssVars before resurrecting. */
  secondary?: string | null
  /** Display/heading font name (resolved to a stack via DISPLAY_FONT_STACKS). */
  display_font?: string | null
  /** Marble texture on the page surface (corp white-label texture). */
  marble?: boolean
  /** Decorative rosemaling ornaments (corners/dividers) — consumed by surfaces. */
  rosemaling?: boolean
}

/**
 * Known display-font stacks with the CORRECT generic fallback (serif fonts must
 * fall back to serif, not the app sans). The «Tema» editor's font select offers
 * these keys; an unknown value falls back to a safe serif-or-sans guess.
 */
export const DISPLAY_FONT_STACKS: Record<string, string> = {
  'Cormorant Garamond': "'Cormorant Garamond', 'Cormorant', Georgia, 'Times New Roman', serif",
  'Playfair Display': "'Playfair Display', Georgia, 'Times New Roman', serif",
  'EB Garamond': "'EB Garamond', Georgia, serif",
  'Fraunces': "'Fraunces', Georgia, serif",
  'Libre Baskerville': "'Libre Baskerville', Georgia, serif",
  'Spectral': "'Spectral', Georgia, serif",
  'Bree': "'Bree', 'Inter', ui-sans-serif, system-ui, sans-serif",
  'Inter': "'Inter', ui-sans-serif, system-ui, sans-serif",
  'Poppins': "'Poppins', 'Inter', ui-sans-serif, sans-serif",
  'Montserrat': "'Montserrat', 'Inter', ui-sans-serif, sans-serif",
}

/** Resolve a display-font name to a full CSS stack (null = use app default). */
export function resolveDisplayFont(name: string | null | undefined): string | null {
  const n = (name ?? '').trim()
  if (!n || n.toLowerCase() === 'inter') return null
  if (DISPLAY_FONT_STACKS[n]) return DISPLAY_FONT_STACKS[n]
  // Unknown: heuristic generic fallback so it never collapses to a bad default.
  const serifish = /garamond|playfair|baskerville|fraunces|spectral|serif|times|georgia/i.test(n)
  return serifish
    ? `'${n}', Georgia, 'Times New Roman', serif`
    : `'${n}', 'Inter', ui-sans-serif, system-ui, sans-serif`
}

const HEX = /^#[0-9a-fA-F]{6}$/

/** True when `theme` uses the v2 flat palette shape. */
export function isFlatTheme(theme: EventTheme | null | undefined): boolean {
  return !!theme && (theme.version === 2 || typeof theme.primary === 'string')
}

export interface FlatThemeResult {
  /** CSS custom properties to apply to the scoped element. */
  vars: Record<string, string>
  /** Whether to paint the marble texture on the page surface. */
  marble: boolean
  /** Which neutral ramp the palette was derived on. */
  mode: 'light' | 'dark'
}

/**
 * Turn a v2 flat theme into the full CSS-variable set (WCAG-safe on-colors) plus
 * a display-font override. Shared by every surface so they render identically.
 * Returns null when there's nothing usable (no valid primary).
 */
export function flatThemeToCssVars(
  theme: EventTheme | null | undefined,
  /**
   * Render the palette for THIS mode instead of the theme's own.
   *
   * The theme's `mode` is its DEFAULT, not a constraint. Its 62 tokens are
   * generated FOR a mode and injected inline on `<html>`, where they beat every
   * class-based rule — so as long as the mode was fixed, the palette was fixed
   * with it, and the theme toggle had to be neutralised or the surface would
   * disagree with the class. Measured 10.08.2026: with the toggle allowed to
   * move the class, `--surface-page` stayed `#0a1018` in light mode, i.e. white
   * text on a dark page under a `light` class.
   *
   * Passing the resolved mode here is what lets both move together, so the
   * event keeps its identity in whichever mode the reader is in.
   */
  modeOverride?: 'light' | 'dark',
): FlatThemeResult | null {
  if (!theme || !theme.primary || !HEX.test(theme.primary)) return null
  const ownMode: 'light' | 'dark' = theme.mode === 'dark' ? 'dark' : 'light'
  const mode: 'light' | 'dark' = modeOverride ?? ownMode
  /**
   * ⚠️ THE ONE LINE THE WHOLE TOGGLE TURNS ON.
   *
   * `generateSemanticTokens` derives the ENTIRE surface/text/border family from
   * `surface` when it is given one, and ignores `mode` while doing so:
   *
   *     const fam = isHex(inputs.surface) ? deriveSurfaceFamily(inputs.surface)
   *                                       : ramp-for-mode
   *
   * So a theme carrying a surface renders the same dark family whatever mode is
   * asked for. That is why three separate attempts at freeing the theme toggle
   * failed (6ae83a2, 104eedd, 68211b0) — every one of them fixed a layer ABOVE
   * this line and could not reach it.
   *
   * A stored `surface` is a statement about the theme's OWN mode: «my dark page
   * is this navy». It says nothing about the other side. So it applies in that
   * mode and is dropped in the other, where the app's own ramp takes over and
   * the event keeps its primary, accent and display font. The event still looks
   * like itself; it simply stops dictating a page colour it was never asked to
   * describe.
   *
   * The alternative — mirroring the navy into a cream — is a design decision
   * about someone's brand, and code must not make it quietly.
   */
  const surfaceForMode = mode === ownMode ? (theme.surface ?? null) : null
  const vars = generateSemanticTokens(
    {
      primary: theme.primary,
      accent: theme.accent ?? null,
      surface: surfaceForMode,
      font: theme.display_font ?? null,
    },
    mode,
  )
  // NB: `secondary` is still parsed/preserved for stored themes, but no longer
  // emitted as --color-secondary — nothing anywhere consumed that var (measured:
  // zero var(--color-secondary) references in app or DS2 CSS). The editor field
  // that wrote it is gone; re-add BOTH together if a real consumer appears.
  // Display/heading font — override only the display stack, keep body readable.
  // The editorial hero (--font-hero) inherits it too, so a white-label event's
  // title uses its own brand face rather than the app's default Fraunces serif.
  const displayStack = resolveDisplayFont(theme.display_font)
  if (displayStack) {
    vars['--font-display'] = displayStack
    vars['--font-hero'] = displayStack
  }
  return { vars, marble: !!theme.marble, mode }
}

/** Flat org brand returned by get_brand_settings_for_event. */
export interface OrgBrand {
  primary_color?: string | null
  font_family?: string | null
  default_theme?: string | null
  surface_texture?: string | null
}

/**
 * The light/dark side a tenant has actually STATED — or null for «never said».
 *
 * `brand_settings.default_theme` is `not null default 'light'`
 * (20260603_brand_theme_and_public_event_brand.sql) and has **no editor field
 * anywhere** — it is concierge-set. So the column cannot tell «this tenant
 * chose light» from «nobody ever touched this row», and every brand_settings
 * row that was never edited reads `'light'`. Measured 12.08.2026: three rows,
 * and only Nortura PROFF carries a value somebody deliberately wrote (`dark`).
 *
 * Treating that column default as a stated preference is the same trap
 * `profiles.theme_preference` («NOT NULL DEFAULT 'system'») sprang in
 * `hydrateTheme` — it turns «unset» into «chosen» and overrides people who
 * never asked for it. Here it would pin every branded tenant's members to
 * light even on a dark OS. So: `'dark'` is stated, everything else is silence.
 *
 * ⚠️ THIS IS NOT A CAP ON THE FEATURE, it is a reading of the storage. The
 * moment a tenant can STATE light in the branding editor, make the column
 * nullable, backfill the untouched rows to NULL, and widen this function —
 * field, column and reader together, or none of them (the same rule
 * `docs/THEMING.md` states for `--color-secondary`).
 */
export function statedTenantMode(defaultTheme: string | null | undefined): 'dark' | null {
  return defaultTheme === 'dark' ? 'dark' : null
}

export interface ResolvedSide {
  primaryHex: string | null
  bgColor: string | null
  bgImage: string | null
}
export interface ResolvedDesign {
  modes: ('light' | 'dark')[] // which modes this event offers (1 = locked, 2 = toggle)
  initialMode: 'light' | 'dark'
  fontFamily: string | null
  light: ResolvedSide
  dark: ResolvedSide
}

/**
 * Marble backdrop: texture asset + readability scrim per mode. ONE definition —
 * this exact pair was previously duplicated in useEventThemeOnDocument,
 * usePublicEventBrand (twice) and a dead index.css block, and the copies had
 * already drifted on the scrim's second stop.
 */
export const MARBLE_TEXTURES: Record<'light' | 'dark', string> = {
  light: '/brand/textures/marble-light.jpg',
  dark: '/brand/textures/marble-dark.jpg',
}
export const MARBLE_SCRIMS: Record<'light' | 'dark', string> = {
  light: 'linear-gradient(rgba(255,255,255,0.42),rgba(255,255,255,0.58))',
  dark: 'linear-gradient(rgba(13,12,11,0.55),rgba(13,12,11,0.72))',
}

const PRESET_TEXTURES: Record<string, string> = {
  'marble-light': '/brand/textures/marble-light.jpg',
  'marble-dark': '/brand/textures/marble-dark.jpg',
  'concrete-light': '/brand/textures/concrete-light.jpg',
  'concrete-dark': '/brand/textures/concrete-dark.jpg',
  'metal': '/brand/textures/metal.jpg',
}

/** Map a stored bg_image value (preset key or URL) to a usable URL. */
export function resolveBgImage(v: string | null | undefined): string | null {
  if (!v) return null
  return PRESET_TEXTURES[v] ?? v
}

function sideHasContent(s: EventThemeSide | undefined): boolean {
  return !!(s && (s.primary || s.bg_color || s.bg_image))
}

/**
 * Merge an event theme over the org brand and decide the available modes.
 * "Smart" (mode 'auto' or absent): both sides with content → light+dark
 * toggle; one side → that single mode; neither → fall back to the org's
 * default_theme as a single mode using org colors.
 */
export function resolveEventDesign(org: OrgBrand | null, theme: EventTheme | null): ResolvedDesign {
  const orgPrimary = org?.primary_color ?? null
  const orgFont = org?.font_family ?? null
  const orgTexture = org?.surface_texture ?? null
  // Same reading as the app shell's tenant default — ONE definition, so the
  // public token route and the signed-in shell can never disagree about which
  // side a tenant asked for. `?? 'light'` because this path needs a concrete
  // side to offer as the single fallback mode.
  const orgDefault: 'light' | 'dark' = statedTenantMode(org?.default_theme) ?? 'light'

  const side = (m: 'light' | 'dark'): ResolvedSide => {
    const t = theme?.[m]
    const other = theme?.[m === 'light' ? 'dark' : 'light']
    return {
      // Falls back to the OTHER side's primary before the org's. A theme that
      // only fills in its dark side still has a brand colour, and rendering the
      // light side in the ORG's primary instead would silently swap one brand
      // for another the moment a reader switched mode.
      primaryHex: t?.primary || other?.primary || orgPrimary,
      bgColor: t?.bg_color || null,
      bgImage: resolveBgImage(t?.bg_image) ||
        // org marble texture maps to the matching mode's preset
        (orgTexture ? resolveBgImage(`${orgTexture}-${m}`) ?? resolveBgImage(orgTexture) : null),
    }
  }

  const light = side('light')
  const dark = side('dark')

  let modes: ('light' | 'dark')[]
  const explicit = theme?.mode
  if (explicit === 'light') modes = ['light']
  else if (explicit === 'dark') modes = ['dark']
  else if (explicit === 'both') modes = ['light', 'dark']
  else {
    // auto
    const hasLight = sideHasContent(theme?.light)
    const hasDark = sideHasContent(theme?.dark)
    if (hasLight && hasDark) modes = ['light', 'dark']
    else if (hasLight) modes = ['light']
    else if (hasDark) modes = ['dark']
    else modes = [orgDefault] // no per-event design → org default, single
  }

  const initialMode = modes.includes(orgDefault) ? orgDefault : modes[0]
  return { modes, initialMode, fontFamily: theme?.font_family || orgFont, light, dark }
}
